/**
 * Which user and token a parent message acts with (tss-api #6, D10).
 *
 * - `legacy`: the parent sent `accessToken` (an older SDK, or an updated SDK still on a session stored by an
 *   older one), or the channel was opened by an SDK that predates iframe sessions. Everything works as before
 *   with the parent's token and `userId`; Basic backups are off.
 * - `iframe`: an updated parent without `accessToken` while the iframe holds a session. The parent's
 *   `accessToken` and `userId` are ignored: both come from the iframe session.
 * - `none`: an updated parent and no iframe session. Nothing user-related can run.
 *
 * A session not yet confirmed by the consent step counts only for that step (`includeUnconfirmed`, AUTHENTICATE).
 */

import type { IframeSession } from './iframe-session'

export type MessageAuth =
  | { mode: 'legacy'; userId?: string; accessToken?: string }
  | { mode: 'iframe'; userId: string; accessToken: string }
  | { mode: 'none' }

export { SESSION_FREE_MESSAGES } from '../../lib/session-messages'

/** The consent step: the only message an unconfirmed session acts for */
export const CONSENT_MESSAGE = 'AUTHENTICATE'

/**
 * @param channelSupportsIframeSession the channel was opened by an SDK that announced its version in SDK_AUTH
 */
export async function resolveMessageAuth(
  data: Record<string, any> | undefined,
  channelSupportsIframeSession: boolean,
  session: Pick<IframeSession, 'get' | 'getIncludingUnconfirmed' | 'getAccessToken'>,
  options: { includeUnconfirmed?: boolean } = {}
): Promise<MessageAuth> {
  const parentToken = typeof data?.accessToken === 'string' && data.accessToken ? data.accessToken : undefined
  const parentUserId = typeof data?.userId === 'string' && data.userId ? data.userId : undefined

  if (parentToken || !channelSupportsIframeSession) {
    return { mode: 'legacy', userId: parentUserId, accessToken: parentToken }
  }

  const stored = options.includeUnconfirmed ? session.getIncludingUnconfirmed() : session.get()
  if (!stored) return { mode: 'none' }
  return { mode: 'iframe', userId: stored.userId, accessToken: await session.getAccessToken() }
}

/**
 * Message data as the handlers see it: with an iframe session the parent's `accessToken` and `userId` are
 * replaced by the session's; with none they are dropped.
 */
export function applyMessageAuth(data: Record<string, any> | undefined, auth: MessageAuth): Record<string, any> {
  const base = { ...(data ?? {}) }
  if (auth.mode === 'legacy') return base
  delete base.accessToken
  delete base.userId
  if (auth.mode === 'none') return base
  return { ...base, userId: auth.userId, accessToken: auth.accessToken }
}

/** Basic (server-managed) backups run only on an iframe session (D12) */
export function basicBackupAllowed(auth: MessageAuth): boolean {
  return auth.mode === 'iframe'
}
